import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLayout } from '@/contexts/LayoutContext';
import { UnifiedContactProvider } from '@/contexts/UnifiedContactContext';
import Navigation from './Navigation';
import BottomNav from './BottomNav';
import GlobalUnifiedContactModal from '@/components/messaging/GlobalUnifiedContactModal';
import CookieConsentBanner from '@/components/CookieConsentBanner';


import { useIsDesktop } from '@/hooks/use-tablet';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const { isNativeApp } = useLayout();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  // Global listener for require-auth events dispatched by auth-gated components
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const message = detail?.message || 'Please sign in to continue';

      toast({
        title: 'Sign In Required',
        description: message,
      });

      const currentPath = window.location.pathname + window.location.search;
      navigate(`/auth?redirect=${encodeURIComponent(currentPath)}`);
    };

    window.addEventListener('require-auth', handler);
    return () => window.removeEventListener('require-auth', handler);
  }, [navigate]);
  const location = useLocation();
  
  // Pages that have their own navigation/header (wizards, homepage)
  // Hide bottom nav on ALL wizard pages to prevent overlap
  const isHelicopterWizard = location.pathname === '/create-listing' || location.pathname.startsWith('/create-listing/');
  const isEngineWizard = location.pathname.startsWith('/create-listing-engine');
  const isPartsWizard = location.pathname.startsWith('/create-listing-parts');
  const isMobileCreate = location.pathname.startsWith('/mobile-create');
  const isMobileAssetSelector = location.pathname === '/mobile-asset-selector';
  const isHomepage = location.pathname === '/';
  const isMarketplace = location.pathname === '/marketplace';
  
  // Always show bottom nav on mobile - wizards have their own footer that sits above it
  const showBottomNav = true;
  
  // Hide top nav in native app mode OR on mobile/tablet (homepage has its own MobileTopNav)
  // Only desktop (≥1024px) shows Navigation
  const showTopNav = !isNativeApp && isDesktop;
  
  // On homepage, Navigation is handled inside Index.tsx for desktop
  const skipTopNav = isHomepage || location.pathname === '/auth'
    || location.pathname.startsWith('/engines')
    || location.pathname.startsWith('/parts-tools')
    || location.pathname.startsWith('/jobs')
    || location.pathname.startsWith('/services')
    || location.pathname.startsWith('/marketplace');

  return (
    <UnifiedContactProvider>
      <div className={cn(
        'min-h-screen',
        showBottomNav && 'lg:pb-0 pb-bottom-nav' // Add padding for bottom nav only on mobile/tablet
      )}>
        {/* Top Navigation - Hidden on mobile/tablet, shown on desktop (except homepage handles its own) */}
        {showTopNav && !skipTopNav && <Navigation />}
        
        {/* Main Content */}
        <main className={cn(
          isNativeApp && 'pt-0'
        )}>
          {children}
        </main>
        
        {/* Bottom Navigation - CSS handles lg:hidden */}
        {showBottomNav && <BottomNav />}
      </div>
      
      {/* Global Unified Contact Modal - rendered at top level to avoid nesting issues */}
      <GlobalUnifiedContactModal />
      
      {/* Cookie Consent Banner */}
      <CookieConsentBanner />
    </UnifiedContactProvider>
  );
}
